import React from 'react';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import ReactDOMServer from 'react-dom/server';
import HoverTooltip from 'src/components/BeaconV2Components/HoverTooltip/HoverTooltip';
import { useStacklineTheme } from '@stackline/ui';
import { ComparisonPeriod, useComparisonPeriod, useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { getFormattedMetricDateLabel } from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import { getPreviousYearWeekId, timestampToWeekId } from 'src/utils/dateUtils';
import { useWeeklyPromotionDataForSpline } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/hooks';
import PromotionHoverCard from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/PromotionHoverCard';

export const BeaconSplineChartWithPromotionsWrapper = ({
  indexName,
  fieldName
}: {
  indexName: string;
  fieldName: string;
}) => {
  const formatMetric = useMetricFormatter();
  const comparisonPeriod = useComparisonPeriod();
  const theme = useStacklineTheme();
  const { isProductEntity } = useProductEntity();
  const weeklyData = useWeeklyPromotionDataForSpline(isProductEntity);
  const isPriorPeriod = comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD;

  return (
    <BeaconComparableSplineChart
      splineChartProps={{
        modifySeries(series) {
          // At the product level, we modify the series to enable marker for weeks with promotions
          const [forecastSeries, primarySeries, comparisonSeries] = series;

          const newPrimarySeries = {
            ...primarySeries,
            data: primarySeries?.data?.map((point) => {
              const weekId = timestampToWeekId(point.x);

              // Only enable promotion marker for product entities and weeks with promotion data
              const enablePromotionMarker =
                isProductEntity && weeklyData && Object.keys(weeklyData).includes(weekId.toString());

              // Unique static marker for weeks with promotions
              return {
                ...point,
                marker: {
                  ...(enablePromotionMarker
                    ? {
                        enabled: enablePromotionMarker,
                        symbol: 'circle',
                        radius: 3,
                        width: 6,
                        height: 6,
                        states: { hover: { enabled: true } }
                      }
                    : point.marker)
                }
              };
            })
          };

          const newComparisonSeries = {
            ...comparisonSeries,
            data: comparisonSeries?.data?.map((point) => {
              const weekId = timestampToWeekId(point?.x);
              const previousYearWeekId = getPreviousYearWeekId(weekId);
              const weekToUse = isPriorPeriod ? weekId : previousYearWeekId;

              // Only enable promotion marker for product entities and weeks with promotion data
              const enablePromotionMarker =
                isProductEntity && weeklyData && Object.keys(weeklyData).includes(weekToUse.toString());
              // Unique static marker for weeks with promotions
              return {
                ...point,
                marker: {
                  ...(enablePromotionMarker
                    ? {
                        enabled: enablePromotionMarker,
                        symbol: 'circle',
                        radius: 3,
                        width: 6,
                        height: 6,
                        states: { hover: { enabled: true } }
                      }
                    : point.marker)
                }
              };
            })
          };

          return [forecastSeries, newPrimarySeries, newComparisonSeries];
        },

        renderCustomTooltip({ primaryPoint, secondaryPoint, percentChange, weekId, metricType }) {
          const previousYearWeekId = getPreviousYearWeekId(timestampToWeekId(primaryPoint?.x));
          // If we're in the product view and have promotion data for the week, show the promotion tooltip for that data point
          if (isProductEntity && weeklyData && (weeklyData[weekId] || weeklyData[previousYearWeekId])) {
            return ReactDOMServer.renderToStaticMarkup(
              <PromotionHoverCard
                primaryPromotionData={weeklyData[weekId] || weeklyData[previousYearWeekId]}
                primaryPoint={primaryPoint}
                secondaryPoint={secondaryPoint}
                percentChange={percentChange}
                weekId={weeklyData[weekId] ? weekId : previousYearWeekId}
                metricType={metricType}
                isProductEntity={isProductEntity}
                comparisonPeriod={comparisonPeriod}
                theme={theme}
                formatMetric={formatMetric}
              />
            );
          } else {
            return ReactDOMServer.renderToStaticMarkup(
              <HoverTooltip
                theme={theme}
                title={`Week ${weekId % 100}`}
                primaryColor={(primaryPoint.color as string) || theme.colors.primary}
                primaryMetric={formatMetric(primaryPoint.y, metricType, {
                  decimalPlaces: 2,
                  showFullValue: metricType === METRICTYPE.PERCENT,
                  showNegative: true
                })}
                primaryMetricLabel={getFormattedMetricDateLabel(
                  weekId,
                  undefined,
                  primaryPoint.series.index === 2 && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR // the secondary series will always have index 2, subtract a year if comparing against prior year
                )}
                change={
                  percentChange !== undefined
                    ? formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
                    : undefined
                } // get rid of negative sign if there is one
                rawChange={percentChange}
                secondaryMetric={
                  secondaryPoint
                    ? formatMetric(secondaryPoint.y, metricType, {
                        decimalPlaces: 2,
                        showFullValue: metricType === METRICTYPE.PERCENT,
                        showNegative: true
                      })
                    : ''
                }
                secondaryMetricLabel={getFormattedMetricDateLabel(
                  weekId,
                  undefined,
                  comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
                )}
                secondaryColor={secondaryPoint ? (secondaryPoint.color as string) : theme.colors.primaryLight}
              />
            );
          }
        }
      }}
      indexName={indexName}
      fieldName={fieldName}
    />
  );
};
